import { useState } from "react";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import FileTemplateSelect from "components/file-template-select/file-template-select";
import {
  updateFinanceSettings,
  updateSanctionsSettings,
  updateVatCheckSettings,
} from "services/company.service";
import {
  ChangeLink,
  EditPanel,
  SettingRow,
  SettingsCard,
  Toggle,
  useInlineEdit,
  useSaveWithToast,
} from "./ui";
import { EvidenceCheck, EvidenceCheckRow } from "./evidence-check-row";
import { useSettingNames } from "./use-setting-names";

type FinanceSettings = {
  approvalsEnabled: boolean;
  /** The document the customer signs each year. */
  template: string;
  skipQuestionnaireIfFilledBefore: boolean;
};

/** The sanctions-settings endpoint rewrites all of this on every save. */
type SanctionsSettings = {
  isEuSanctionsEnabled: boolean;
  isUsaSanctionsEnabled: boolean;
  isLtSanctionsEnabled: boolean;
  sanctionsFileCategoryEu: string;
  sanctionsFileCategoryUsa: string;
  sanctionsFileCategoryLt: string;
  sanctionsFileTypeEu: string;
  sanctionsFileTypeUsa: string;
  sanctionsFileTypeLt: string;
};

// One row per sanctions list: its texts, and which fields of SanctionsSettings
// hold its switch and the place its proof is filed.
const SANCTION_LISTS = [
  {
    labelKey: "settings_check_eu_sanctions",
    descriptionKey: "settings_check_eu_sanctions_hint",
    enabled: "isEuSanctionsEnabled",
    category: "sanctionsFileCategoryEu",
    fileType: "sanctionsFileTypeEu",
  },
  {
    labelKey: "settings_check_usa_sanctions",
    descriptionKey: "settings_check_usa_sanctions_hint",
    enabled: "isUsaSanctionsEnabled",
    category: "sanctionsFileCategoryUsa",
    fileType: "sanctionsFileTypeUsa",
  },
  {
    labelKey: "settings_check_lt_sanctions",
    descriptionKey: "settings_check_lt_sanctions_hint",
    enabled: "isLtSanctionsEnabled",
    category: "sanctionsFileCategoryLt",
    fileType: "sanctionsFileTypeLt",
  },
] as const;

function readFinanceSettings(company: any): FinanceSettings {
  return {
    approvalsEnabled: company.isFinanceApprovalsEnabled !== false,
    template: company.financeTemplate || "",
    skipQuestionnaireIfFilledBefore:
      company.skipQuestionnaireRequirementIfCompletedInPast ?? true,
  };
}

function readSanctionsSettings(company: any): SanctionsSettings {
  // Before the three lists got their own category, one category served all of
  // them; keep showing it until the first save writes the new ones.
  const neverMigrated =
    company.sanctionsFileCategoryEu == null &&
    company.sanctionsFileCategoryUsa == null &&
    company.sanctionsFileCategoryLt == null;
  const legacyCategory = neverMigrated
    ? company.sanctionsFileCategory ?? ""
    : "";
  return {
    isEuSanctionsEnabled: company.isEuSanctionsEnabled ?? false,
    isUsaSanctionsEnabled: company.isUsaSanctionsEnabled ?? false,
    isLtSanctionsEnabled: company.isLtSanctionsEnabled ?? false,
    sanctionsFileCategoryEu: company.sanctionsFileCategoryEu ?? legacyCategory,
    sanctionsFileCategoryUsa:
      company.sanctionsFileCategoryUsa ?? legacyCategory,
    sanctionsFileCategoryLt: company.sanctionsFileCategoryLt ?? legacyCategory,
    sanctionsFileTypeEu: company.sanctionsFileTypeEu ?? "",
    sanctionsFileTypeUsa: company.sanctionsFileTypeUsa ?? "",
    sanctionsFileTypeLt: company.sanctionsFileTypeLt ?? "",
  };
}

/** The endpoints store "nothing chosen" as null, not as an empty string. */
function emptyChoicesToNull(settings: object) {
  return Object.fromEntries(
    Object.entries(settings).map(([field, value]) => [
      field,
      value === "" ? null : value,
    ])
  );
}

interface CustomerTermsSettingsProps {
  companyEntity: any;
  /** Loads the company again; a save is done when that has finished. */
  onRefresh: () => Promise<unknown>;
}

export default function CustomerTermsSettings({
  companyEntity,
  onRefresh,
}: CustomerTermsSettingsProps) {
  const { t } = useTranslation();
  const { companyId = "" } = useParams<{ companyId: string }>();
  const names = useSettingNames(companyId);

  const finance = readFinanceSettings(companyEntity);
  const sanctions = readSanctionsSettings(companyEntity);

  const templateEdit = useInlineEdit(finance.template);
  const switchSave = useSaveWithToast();
  const templateSave = useSaveWithToast();
  // A sanctions save sends all nine fields as they stand on the page. The three
  // rows stay locked until it has finished and the page shows the result; a
  // second save built before that would send the first one's old value back.
  const [sanctionsSaving, setSanctionsSaving] = useState(false);

  // Switching approvals on with no document saved yet opens the document editor
  // instead of saving; the rest of the page shows while that editor is open.
  const approvalsShown = finance.approvalsEnabled || templateEdit.editing;

  // Nothing to look up when no document is saved; otherwise wait for the names.
  const templateName =
    !finance.template || names.ready
      ? names.templateName(finance.template) || t("settings_none")
      : null;

  // `saver` is the save hook whose saving state and error the caller shows:
  // the switches and the document editor each have their own.
  const saveFinance = (
    changes: Partial<FinanceSettings>,
    saver: typeof switchSave
  ) => {
    const next = { ...finance, ...changes };
    return saver.save(
      () =>
        updateFinanceSettings(
          {
            isFinanceApprovalsEnabled: next.approvalsEnabled,
            financeTemplate: next.template,
            skipQuestionnaireRequirementIfCompletedInPast:
              next.skipQuestionnaireIfFilledBefore,
          },
          companyId
        ).then(onRefresh),
      {
        errorMessage: (err: any) =>
          err?.response?.data === "FINANCE_DATA_REQUIRED"
            ? t("settings_customer_terms_template_required")
            : t("settings_save_failed"),
      }
    );
  };

  const changeApprovals = async (enabled: boolean) => {
    if (enabled && !finance.template) {
      templateEdit.start();
      return;
    }
    if (await saveFinance({ approvalsEnabled: enabled }, switchSave)) {
      templateEdit.cancel();
    }
  };

  const saveTemplate = async () => {
    const saved = await saveFinance(
      { approvalsEnabled: true, template: templateEdit.draft },
      templateSave
    );
    if (saved) templateEdit.cancel();
  };

  const saveSanctions = async (next: SanctionsSettings) => {
    setSanctionsSaving(true);
    try {
      await updateSanctionsSettings(emptyChoicesToNull(next), companyId).then(
        onRefresh
      );
    } finally {
      setSanctionsSaving(false);
    }
  };

  const saveVatCheck = (next: EvidenceCheck) =>
    updateVatCheckSettings(
      emptyChoicesToNull({
        isVatCheckEnabled: next.enabled,
        vatCheckFileCategory: next.categoryId,
        vatCheckFileType: next.fileTypeId,
      }),
      companyId
    ).then(onRefresh);

  return (
    <div className="space-y-5">
      <SettingsCard>
        <SettingRow
          label={t("settings_customer_terms_approvals_label")}
          description={t("settings_customer_terms_approvals_hint")}
          action={
            <Toggle
              label={t("settings_customer_terms_approvals_label")}
              checked={approvalsShown}
              disabled={switchSave.saving}
              onChange={changeApprovals}
            />
          }
        />
      </SettingsCard>

      {approvalsShown && (
        <>
          <SettingsCard title={t("settings_customer_terms_document_card")}>
            <SettingRow
              label={t("settings_customer_terms_document_label")}
              description={t("settings_customer_terms_document_hint")}
              value={templateName}
              action={
                !templateEdit.editing && (
                  <ChangeLink onClick={templateEdit.start} />
                )
              }
            >
              {templateEdit.editing && (
                <EditPanel
                  saving={templateSave.saving}
                  error={templateSave.error}
                  onSave={saveTemplate}
                  onCancel={templateEdit.cancel}
                >
                  <FileTemplateSelect
                    value={templateEdit.draft}
                    onChange={templateEdit.setDraft}
                    isRequired
                    viewAll
                  />
                </EditPanel>
              )}
            </SettingRow>
            <SettingRow
              label={t("settings_customer_terms_skip_questionnaire_label")}
              description={t("settings_customer_terms_skip_questionnaire_hint")}
              action={
                <Toggle
                  label={t("settings_customer_terms_skip_questionnaire_label")}
                  checked={finance.skipQuestionnaireIfFilledBefore}
                  disabled={switchSave.saving}
                  onChange={(skip) =>
                    saveFinance(
                      { skipQuestionnaireIfFilledBefore: skip },
                      switchSave
                    )
                  }
                />
              }
            />
          </SettingsCard>

          <SettingsCard
            title={t("settings_customer_terms_checks_card")}
            description={t("settings_customer_terms_checks_hint")}
          >
            {SANCTION_LISTS.map((list) => (
              <EvidenceCheckRow
                key={list.enabled}
                label={t(list.labelKey)}
                description={t(list.descriptionKey)}
                check={{
                  enabled: sanctions[list.enabled],
                  categoryId: sanctions[list.category],
                  fileTypeId: sanctions[list.fileType],
                }}
                names={names}
                locked={sanctionsSaving}
                onSave={(next) =>
                  saveSanctions({
                    ...sanctions,
                    [list.enabled]: next.enabled,
                    [list.category]: next.categoryId,
                    [list.fileType]: next.fileTypeId,
                  })
                }
              />
            ))}
            <EvidenceCheckRow
              label={t("settings_check_vat")}
              description={t("settings_check_vat_hint")}
              check={{
                enabled: companyEntity.isVatCheckEnabled ?? false,
                categoryId: companyEntity.vatCheckFileCategory ?? "",
                fileTypeId: companyEntity.vatCheckFileType ?? "",
              }}
              names={names}
              onSave={saveVatCheck}
            />
          </SettingsCard>
        </>
      )}
    </div>
  );
}
